'use client'

import { useAtomValue } from 'jotai'
import dynamic from 'next/dynamic'
import { memo, useEffect, useState } from 'react'

import { ChartSkeleton } from '@/features/chart/components/widgets/chart-skeleton'
import TVChartWidget, {
  type ChartWidgetProps,
} from '@/features/chart/components/widgets/tv-chart-widget'
import { chartDataTimeframe, isTickTimeframe } from '@/lib/chart-timeframe'
import { chartEngineAtom } from '@/state/storage'

export type { ChartWidgetProps } from './tv-chart-widget'

// Percept (TitanCharts, WebGL) is opt-in via the chart-engine setting, so it's split into its own
// chunk and only fetched by users who pick it.
const ChartPerceptWidget = dynamic(
  () => import('@/features/chart/components/widgets/chart-percept-widget'),
  { ssr: false, loading: () => <ChartSkeleton /> }
)

// TVChartWidget is bundled eagerly (it's the default/primary widget, essentially
// always on screen) but can't render during SSR — it depends on `window.TradingView`,
// injects a <script> tag, and reaches into iframe internals. Gate the real render on
// client mount instead of code-splitting it behind next/dynamic: the app already has
// a global loading indicator, so there's no need for a chart-specific skeleton or the
// extra chunk-fetch round trip on top of it.
function ChartWidget({ timeframe, ...props }: ChartWidgetProps) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const engine = useAtomValue(chartEngineAtom)

  // The picker value can be Tick, which isn't a bar width: both engines fetch and stream
  // the 1s bars for it and only switch how they draw them.
  const tick = isTickTimeframe(timeframe)
  const engineProps = { ...props, timeframe: chartDataTimeframe(timeframe), tick }

  if (!mounted) return <ChartSkeleton />
  if (engine === 'percept') return <ChartPerceptWidget {...engineProps} />
  return <TVChartWidget {...engineProps} />
}

// Memoized so a parent re-render triggered by unrelated linking-group state (this
// widget subscribes narrowly via useLinkingGroupWatchList/useLinkingGroupChartOption,
// but TabGroupedChart itself still subscribes to the full slice) doesn't cascade into
// remounting/re-rendering this — and, more importantly, doesn't defeat the "keep the TV
// widget mounted across option-mode toggles" change in TabGroupedChart, which relies on
// this component's own re-render being skippable when its props haven't changed.
export default memo(ChartWidget)
